import React, { useState, useEffect, useCallback } from "react";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import API_URL from "../../api/API";

export const AthletePurchaseTransactions = () => {
  const { athlete } = useAthleteAuthContext();
  const [transactions, setTransactions] = useState({
    speed: [],
    strength: [],
    tools: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchTransactions = useCallback(async () => {
    if (!athlete) return;

    try {
      const res = await fetch(
        `${API_URL.GET_ATHLETE_PURCHASE_DATA}${athlete.id}`,
        {
          headers: { Authorization: `Bearer ${athlete.token}` },
        }
      );
      const json = await res.json();

      if (res.ok) {
        setTransactions({
          speed: json.data?.speed || [],
          strength: json.data?.strength || [],
          tools: json.data?.tools || [],
        });
        setError(""); // clear error if data fetched successfully
      } else {
        throw new Error(json.error || "Failed to fetch transactions");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load transactions. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [athlete]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Format date nicely
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleDateString() + " " + d.toLocaleTimeString();
  };

  const renderTransactionList = (list, type) => {
    if (!list || list.length === 0) return null;

    return (
      <div className="mb-6">
        <h3 className="text-xl mb-2">{type}</h3>
        <ul className="bg-smock divide-y divide-gray-200 border border-gray-200 rounded-lg flex flex-col overflow-hidden">
          {list.map((tx) => (
            <li
              key={tx._id}
              className="p-4 xs:p-6 flex flex-col gap-1 xs:gap-2 text-sm xs:text-base"
            >
              <div className="flex flex-col gap-1 xs:gap-2">
                <p className="max-xs:pb-2 max-xs:border-b max-xs:border-grayish/50">
                  {type === "Tools" ? (
                    // `Tool: ${tx.title}`
                    <>
                      Tool:&nbsp;&nbsp;
                      <span className="text-gray-700 font-light max-xs:block max-xs:mt-1">
                         ${tx.title}
                      </span>
                    </>
                  ) : (
                    // `Template: ${tx.title}`
                    <>
                      Template:&nbsp;&nbsp;
                      <span className="text-gray-700 font-light max-xs:block max-xs:mt-1">
                         ${tx.title}
                      </span>
                    </>
                  )}
                </p>
                <p className="max-xs:pb-2 max-xs:border-b max-xs:border-grayish/50">
                  Price:&nbsp;
                  <span className="text-gray-700 font-light max-xs:block max-xs:mt-1"> ${tx.price}</span>
                </p>
                <p className="max-xs:pb-2 max-xs:border-b max-xs:border-grayish/50">
                  Transaction ID:&nbsp;
                  <span className="text-gray-700 font-light max-xs:block max-xs:mt-1">
                    {tx.payment_transaction_id}
                  </span>
                </p>
                <p className="max-xs:pb-2 max-xs:border-b max-xs:border-grayish/50">
                  Status:&nbsp;
                  <span className="text-gray-700 font-light max-xs:block max-xs:mt-1">
                    {tx.payment_status}
                  </span>
                </p>
              </div>
              <div className="mt-0 sm:mt-0">
                Created:&nbsp;
                <span className="text-gray-700 font-light max-xs:block max-xs:mt-1">
                  {formatDate(tx.createdAt)}
                </span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const hasNoTransactions =
    !transactions.speed.length &&
    !transactions.strength.length &&
    !transactions.tools.length;

  return (
    <AthleteLayout>
      <div className="h-full flex flex-col bg-white">
        <div className="flex-1 overflow-y-auto">
          {/*<AthleteHeader />*/}
          <div className="w-full max-w-2xl mx-auto px-4 py-6">
            <h2 className="text-2xl font-bold mb-5">Purchase Transactions</h2>

            {loading ? (
              <p>Loading transactions...</p>
            ) : error ? (
              <p className="text-red-500 mb-4">{error}</p>
            ) : hasNoTransactions ? (
              <p>No transactions available.</p>
            ) : (
              <div className="space-y-6">
                {renderTransactionList(transactions.speed, "Speed Templates")}
                {renderTransactionList(
                  transactions.strength,
                  "Strength Templates"
                )}
                {renderTransactionList(transactions.tools, "Tools")}
              </div>
            )}
          </div>
        </div>
      </div>
    </AthleteLayout>
  );
};
